// Agency service for managing agencies and agents
import { buildApiUrl, debugLog } from '../config/environment';
import { getAuthToken } from './authService';

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// Get all agencies (for listing)
export const getAgencies = async () => {
  debugLog('Fetching agencies...');

  try {
    // Note: This endpoint might need to be adjusted based on actual API
    const response = await fetch(buildApiUrl('/agencies'), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch agencies');
    }

    const data = await response.json();
    debugLog('Agencies fetched:', data);
    return data;
  } catch (error) {
    debugLog('Error fetching agencies:', error);
    throw error;
  }
};

// Get single agency by ID
export const getAgencyById = async (agencyId) => {
  debugLog('Fetching agency:', agencyId);

  try {
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}`), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch agency');
    }

    const data = await response.json();
    debugLog('Agency fetched:', data);
    return data;
  } catch (error) {
    debugLog('Error fetching agency:', error);
    throw error;
  }
};

// Create a new agency
export const createAgency = async (agencyData) => {
  debugLog('Creating agency:', agencyData);

  try {
    const response = await fetch(buildApiUrl('/agencies'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(agencyData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create agency');
    }

    const data = await response.json();
    debugLog('Agency created:', data);
    return data;
  } catch (error) {
    debugLog('Error creating agency:', error);
    throw error;
  }
};

// Update an agency
export const updateAgency = async (agencyId, agencyData) => {
  debugLog('Updating agency:', agencyId, agencyData);

  try {
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}`), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(agencyData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update agency');
    }

    const data = await response.json();
    debugLog('Agency updated:', data);
    return data;
  } catch (error) {
    debugLog('Error updating agency:', error);
    throw error;
  }
};

// Delete an agency
export const deleteAgency = async (agencyId) => {
  debugLog('Deleting agency:', agencyId);

  try {
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete agency');
    }

    debugLog('Agency deleted:', agencyId);
    return true;
  } catch (error) {
    debugLog('Error deleting agency:', error);
    throw error;
  }
};

// Create an agent invitation for an agency.
// The backend sends a verification email (or logs the link in dev) and returns
// without creating the User row. The invitee must click the link to activate
// the agent record. If the email already matches an existing user, `password`
// may be omitted — the server will ignore it.
export const createAgent = async (agencyId, agentData) => {
  debugLog('Inviting agent for agency:', agencyId, agentData);

  try {
    const body = {
      email: agentData.email,
      given: agentData.given,
      surname: agentData.surname,
    };
    if (agentData.mobile) body.mobile = agentData.mobile;
    if (agentData.password) body.password = agentData.password;

    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/agent-invitations`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // The backend may return a text body for http.Error responses.
      const text = await response.text().catch(() => '');
      let message = text;
      try {
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || text;
      } catch (_) { /* keep raw text */ }
      throw new Error(message || 'Failed to invite agent');
    }

    const data = await response.json();
    debugLog('Agent invitation sent:', data);
    return data; // { email, resent, expires_at, message }
  } catch (error) {
    debugLog('Error inviting agent:', error);
    throw error;
  }
};

// Check whether a user already exists with the given email. Requires JWT auth.
// Returns { exists: boolean }. Used by the CreateAgentModal to decide whether
// to show the password field.
export const checkUserExists = async (email) => {
  if (!email) return { exists: false };
  const response = await fetch(
    buildApiUrl(`/users/exists?email=${encodeURIComponent(email)}`),
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    throw new Error('Failed to check email');
  }
  return response.json();
};

// Get agents for an agency
export const getAgencyAgents = async (agencyId) => {
  debugLog('Fetching agents for agency:', agencyId);

  try {
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/agents`), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch agents');
    }

    const data = await response.json();
    debugLog('Agents fetched:', data);
    return data;
  } catch (error) {
    debugLog('Error fetching agents:', error);
    throw error;
  }
};

// Get pending agent invitations for an agency
// Returns an array of { id, email, given, surname, status, is_expired, expires_at, created_at }
export const getAgentInvitations = async (agencyId) => {
  debugLog('Fetching agent invitations for agency:', agencyId);

  try {
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/agent-invitations`), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch agent invitations');
    }

    const data = await response.json();
    debugLog('Agent invitations fetched:', data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    debugLog('Error fetching agent invitations:', error);
    throw error;
  }
};

// Resend (refresh) an existing pending agent invitation
export const resendAgentInvitation = async (agencyId, invitationId) => {
  debugLog('Resending agent invitation:', invitationId);

  try {
    const response = await fetch(
      buildApiUrl(`/agencies/${agencyId}/agent-invitations/${invitationId}/resend`),
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let message = text;
      try {
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || text;
      } catch (_) { /* keep raw text */ }
      throw new Error(message || 'Failed to resend invitation');
    }

    const data = await response.json();
    debugLog('Invitation resent:', data);
    return data;
  } catch (error) {
    debugLog('Error resending invitation:', error);
    throw error;
  }
};

// Remove agent from agency
export const removeAgent = async (agencyId, agentId) => {
  debugLog('Removing agent from agency:', agencyId, agentId);

  try {
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/agents/${agentId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to remove agent');
    }

    debugLog('Agent removed:', agentId);
    return true;
  } catch (error) {
    debugLog('Error removing agent:', error);
    throw error;
  }
};

// Available countries for agencies (country is the primary field, currency derives from it)
export { COUNTRIES } from '../utils/countryUtils';

// Available currencies for agencies (kept for components that need currency list directly)
export const CURRENCIES = [
  { code: 'AUD', name: 'Australian Dollar', symbol: '$' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: '$' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: '$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: '$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
];
